import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import api from "../../../../../api/api";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add,
  Close,
  ContentCopy,
  Download,
  LockReset,
  Person,
  Refresh,
  Search,
  Visibility,
  WorkspacePremium,
} from "@mui/icons-material";

import Swal from "sweetalert2";

import { pdf } from "@react-pdf/renderer";

import CustomerCardInfoPDF from "./CustomerCardInfoPDF";

// =========================================================
// CONSTANTS
// =========================================================

const PAGE_SIZE = 8;

const LEVELS = [
  {
    value: "",
    label: "All Levels",
  },
  {
    value: "NEW",
    label: "New",
  },
  {
    value: "STARTER",
    label: "Starter",
  },
  {
    value: "BRONZE",
    label: "Bronze",
  },
  {
    value: "BRONZE_PLUS",
    label: "Bronze Plus",
  },
  {
    value: "SILVER",
    label: "Silver",
  },
  {
    value: "GOLD",
    label: "Gold",
  },
  {
    value: "VIP",
    label: "VIP",
  },
  {
    value: "ELITE",
    label: "Elite",
  },
];

// =========================================================
// LEVEL CONFIG
// =========================================================

const LEVEL_CONFIG = {
  NEW: {
    label: "New",
    color: "#64748b",
    background: "#f1f5f9",
    border: "#cbd5e1",
  },

  STARTER: {
    label: "Starter",
    color: "#475569",
    background: "#f8fafc",
    border: "#cbd5e1",
  },

  BRONZE: {
    label: "Bronze",
    color: "#92400e",
    background: "#fff7ed",
    border: "#fed7aa",
  },

  BRONZE_PLUS: {
    label: "Bronze Plus",
    color: "#7c2d12",
    background: "#ffedd5",
    border: "#fdba74",
  },

  SILVER: {
    label: "Silver",
    color: "#475569",
    background: "#f1f5f9",
    border: "#cbd5e1",
  },

  GOLD: {
    label: "Gold",
    color: "#a16207",
    background: "#fefce8",
    border: "#fde68a",
  },

  VIP: {
    label: "VIP",
    color: "#92400e",
    background: "#fffbeb",
    border: "#fcd34d",
  },

  ELITE: {
    label: "Elite",
    color: "#111827",
    background: "#f3f4f6",
    border: "#9ca3af",
  },
};

// =========================================================
// LEVEL HELPER
// =========================================================

const getLevelConfig = (level) => {
  return (
    LEVEL_CONFIG[level] || {
      label: level || "Unknown",
      color: "#475569",
      background: "#f8fafc",
      border: "#cbd5e1",
    }
  );
};

// =========================================================
// COMPONENT
// =========================================================

const AdminCustomerTable = () => {
  // =======================================================
  // DATA
  // =======================================================

  const [customers, setCustomers] = useState([]);

  // =======================================================
  // LOADING
  // =======================================================

  const [loading, setLoading] = useState(true);

  const [refreshing, setRefreshing] = useState(false);

  const [pinLoading, setPinLoading] = useState(false);

  const [pdfLoadingId, setPdfLoadingId] =
    useState(null);

  // =======================================================
  // FILTERS
  // =======================================================

  const [search, setSearch] = useState("");

  const [level, setLevel] = useState("");

  // =======================================================
  // PAGINATION
  // =======================================================

  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const [totalCustomers, setTotalCustomers] =
    useState(0);

  // =======================================================
  // PIN MODAL
  // =======================================================

  const [selectedCustomer, setSelectedCustomer] =
    useState(null);

  const [openPinModal, setOpenPinModal] =
    useState(false);

  // =======================================================
  // ERROR
  // =======================================================

  const [error, setError] = useState("");

  // =======================================================
  // REQUEST TRACKING
  // =======================================================

  const requestIdRef = useRef(0);

  // =======================================================
  // FETCH CUSTOMERS
  // =======================================================

  const fetchCustomers = useCallback(
    async ({
      currentPage = 1,
      searchValue = "",
      levelValue = "",
      showRefreshLoader = false,
    } = {}) => {
      const requestId =
        ++requestIdRef.current;

      try {
        if (showRefreshLoader) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const params = {
          page: currentPage,
          page_size: PAGE_SIZE,
        };

        const cleanSearch =
          searchValue.trim();

        if (cleanSearch) {
          params.search = cleanSearch;
        }

        if (levelValue) {
          params.level = levelValue;
        }

        const response = await api.get(
          "/api/customers/admin-list/",
          {
            params,
          }
        );

        // Ignore stale request
        if (
          requestId !==
          requestIdRef.current
        ) {
          return;
        }

        const data =
          response?.data || {};

        const results =
          Array.isArray(data.results)
            ? data.results
            : [];

        const count = Number(
          data.count || 0
        );

        setCustomers(results);

        setTotalCustomers(count);

        setTotalPages(
          count > 0
            ? Math.ceil(
                count / PAGE_SIZE
              )
            : 1
        );

      } catch (err) {
        if (
          requestId !==
          requestIdRef.current
        ) {
          return;
        }

        console.error(
          "ADMIN CUSTOMER FETCH ERROR:",
          err?.response?.data ||
            err
        );

        setCustomers([]);

        setTotalCustomers(0);

        setTotalPages(1);

        setError(
          err?.response?.data
            ?.detail ||
            "Unable to load customers. Please try again."
        );

      } finally {
        if (
          requestId ===
          requestIdRef.current
        ) {
          setLoading(false);

          setRefreshing(false);
        }
      }
    },
    []
  );

  // =======================================================
  // FILTER EFFECT
  // =======================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers({
        currentPage: page,
        searchValue: search,
        levelValue: level,
      });
    }, 350);

    return () => {
      clearTimeout(timer);
    };
  }, [
    page,
    search,
    level,
    fetchCustomers,
  ]);

  // =======================================================
  // SEARCH
  // =======================================================

  const handleSearchChange = (
    event
  ) => {
    setSearch(
      event.target.value
    );

    if (page !== 1) {
      setPage(1);
    }
  };

  // =======================================================
  // LEVEL
  // =======================================================

  const handleLevelChange = (
    event
  ) => {
    setLevel(
      event.target.value
    );

    if (page !== 1) {
      setPage(1);
    }
  };

  // =======================================================
  // PAGE
  // =======================================================

  const handlePageChange = (
    event,
    value
  ) => {
    setPage(value);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =======================================================
  // REFRESH
  // =======================================================

  const handleRefresh = async () => {
    await fetchCustomers({
      currentPage: page,
      searchValue: search,
      levelValue: level,
      showRefreshLoader: true,
    });
  };

  // =======================================================
  // CLEAR FILTERS
  // =======================================================

  const handleClearFilters = () => {
    setSearch("");
    setLevel("");
    setPage(1);
  };

  // =======================================================
  // VIEW PIN
  // =======================================================

  const handleViewPin = async (
    customer
  ) => {
    const confirmation =
      await Swal.fire({
        title: "View Customer PIN?",
        text:
          "Only authorized staff should access customer PIN information.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText:
          "Yes, View PIN",
        cancelButtonText: "Cancel",
        confirmButtonColor:
          "#111827",
        cancelButtonColor:
          "#94a3b8",
      });

    if (
      !confirmation.isConfirmed
    ) {
      return;
    }

    try {
      setPinLoading(true);

      const response =
        await api.get(
          `/api/customers/${customer.id}/`
        );

      setSelectedCustomer(
        response.data
      );

      setOpenPinModal(true);

    } catch (err) {
      console.error(
        "VIEW PIN ERROR:",
        err?.response?.data ||
          err
      );

      Swal.fire(
        "Error",
        err?.response?.data
          ?.detail ||
          "Failed to load customer PIN.",
        "error"
      );

    } finally {
      setPinLoading(false);
    }
  };

  // =======================================================
  // CLOSE PIN MODAL
  // =======================================================

  const handleClosePinModal =
    () => {
      setOpenPinModal(false);

      setTimeout(() => {
        setSelectedCustomer(
          null
        );
      }, 200);
    };

  // =======================================================
  // RESET PIN
  // =======================================================

  const handleResetPin = async (
    customer
  ) => {
    const confirmation =
      await Swal.fire({
        title: "Reset Customer PIN?",
        html: `
          <div style="font-size:14px;color:#64748b;">
            A new 4-digit PIN will be
            generated for
            <strong>${customer.full_name}</strong>.
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText:
          "Yes, Reset PIN",
        cancelButtonText: "Cancel",
        confirmButtonColor:
          "#f97316",
        cancelButtonColor:
          "#94a3b8",
      });

    if (
      !confirmation.isConfirmed
    ) {
      return;
    }

    try {
      setPinLoading(true);

      const response =
        await api.post(
          `/api/customers/${customer.id}/reset-pin/`
        );

      const newPin =
        response?.data?.new_pin;

      await Swal.fire({
        title: "PIN Reset Successfully",
        html: `
          <div style="padding:10px 0;">
            <div style="
              color:#64748b;
              font-size:13px;
              margin-bottom:10px;
            ">
              New Royalty Card PIN
            </div>

            <div style="
              font-size:34px;
              font-weight:800;
              letter-spacing:10px;
              color:#111827;
              background:#f8fafc;
              border-radius:12px;
              padding:15px;
            ">
              ${newPin || "----"}
            </div>
          </div>
        `,
        icon: "success",
        confirmButtonText:
          "Done",
        confirmButtonColor:
          "#111827",
      });

      await fetchCustomers({
        currentPage: page,
        searchValue: search,
        levelValue: level,
      });

    } catch (err) {
      console.error(
        "RESET PIN ERROR:",
        err?.response?.data ||
          err
      );

      Swal.fire(
        "Error",
        err?.response?.data
          ?.error ||
          err?.response?.data
          ?.detail ||
          "Failed to reset customer PIN.",
        "error"
      );

    } finally {
      setPinLoading(false);
    }
  };

  // =======================================================
  // COPY PIN
  // =======================================================

  const handleCopyPin = async () => {
    if (
      !selectedCustomer?.card_pin
    ) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        String(
          selectedCustomer.card_pin
        )
      );

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "PIN copied",
        showConfirmButton: false,
        timer: 1600,
      });

    } catch {
      Swal.fire(
        "Error",
        "Unable to copy PIN.",
        "error"
      );
    }
  };

  // =======================================================
  // DOWNLOAD CUSTOMER CARD
  // =======================================================

  const handleDownloadPDF =
    async (customer) => {
      try {
        setPdfLoadingId(
          customer.id
        );

        Swal.fire({
          title:
            "Generating Royalty Card",
          text:
            "Preparing the customer's printable card...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response =
          await api.get(
            `/api/customers/${customer.id}/`
          );

        const fullCustomer =
          response.data;

        const blob =
          await pdf(
            <CustomerCardInfoPDF
              customer={
                fullCustomer
              }
            />
          ).toBlob();

        const url =
          URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href = url;

        link.download =
          `${fullCustomer.full_name}_Zuluwhiskey_Royalty_Card.pdf`;

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        URL.revokeObjectURL(
          url
        );

        Swal.close();

        await Swal.fire({
          title: "Card Ready",
          text:
            "The customer's royalty card has been generated successfully.",
          icon: "success",
          confirmButtonColor:
            "#111827",
        });

      } catch (err) {
        console.error(
          "DOWNLOAD CARD ERROR:",
          err
        );

        Swal.close();

        Swal.fire(
          "Error",
          "Failed to generate customer card.",
          "error"
        );

      } finally {
        setPdfLoadingId(null);
      }
    };

  // =======================================================
  // TABLE SKELETON
  // =======================================================

  const renderSkeletonRows =
    () => {
      return Array.from({
        length: PAGE_SIZE,
      }).map((_, index) => (
        <TableRow key={index}>
          {Array.from({
            length: 5,
          }).map(
            (_, cellIndex) => (
              <TableCell
                key={cellIndex}
              >
                <Skeleton
                  variant="text"
                  width={
                    cellIndex === 4
                      ? 240
                      : "80%"
                }
                height={28}
              />
            </TableCell>
          ))}
        </TableRow>
      ));
    };

  // =======================================================
  // DISPLAY RANGE
  // =======================================================

  const firstRecord =
    totalCustomers === 0
      ? 0
      : (page - 1) *
          PAGE_SIZE +
        1;

  const lastRecord =
    Math.min(
      page * PAGE_SIZE,
      totalCustomers
    );

  // =======================================================
  // ACTIVE FILTER
  // =======================================================

  const hasFilters =
    Boolean(
      search.trim() ||
        level
    );

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },
        background:
          "linear-gradient(135deg,#f8fafc 0%,#eef2ff 50%,#f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          maxWidth: 1450,
          mx: "auto",
        }}
      >
        {/* ===================================================
            MAIN PANEL
        =================================================== */}

        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: {
              xs: 2,
              md: 3,
            },
            border:
              "1px solid #e5e7eb",
            background:
              "rgba(255,255,255,0.96)",
            boxShadow:
              "0 20px 60px rgba(15,23,42,0.08)",
          }}
        >
          {/* =================================================
              HERO HEADER
          ================================================= */}

          <Box
            sx={{
              position:
                "relative",
              overflow:
                "hidden",
              px: {
                xs: 2,
                md: 4,
              },
              py: {
                xs: 2.5,
                md: 3.5,
              },
              background:
                "linear-gradient(135deg,#111827 0%,#1e293b 60%,#334155 100%)",
              color: "#fff",
            }}
          >
            {/* Decorative glow */}

            <Box
              sx={{
                position:
                  "absolute",
                width: 220,
                height: 220,
                borderRadius:
                  "50%",
                right: -90,
                top: -120,
                background:
                  "rgba(249,115,22,0.22)",
                filter:
                  "blur(30px)",
              }}
            />

            <Box
              sx={{
                position:
                  "absolute",
                width: 180,
                height: 180,
                borderRadius:
                  "50%",
                left: -100,
                bottom: -130,
                background:
                  "rgba(59,130,246,0.16)",
                filter:
                  "blur(30px)",
              }}
            />

            <Box
              sx={{
                position:
                  "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
                justifyContent:
                  "space-between",
                alignItems: {
                  xs: "flex-start",
                  md: "center",
                },
                gap: 2,
              }}
            >
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: 2,
                      display:
                        "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      background:
                        "rgba(255,255,255,0.1)",
                      border:
                        "1px solid rgba(255,255,255,0.15)",
                    }}
                  >
                    <Person
                      sx={{
                        fontSize: 25,
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: {
                          xs: 20,
                          md: 25,
                        },
                        fontWeight: 800,
                        lineHeight: 1.1,
                        letterSpacing:
                          "-0.5px",
                      }}
                    >
                      Customer Management
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.5,
                        color:
                          "rgba(255,255,255,0.68)",
                        fontSize: 13,
                      }}
                    >
                      Manage royalty members,
                      PINs and loyalty cards
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Button
                variant="outlined"
                startIcon={
                  refreshing ? (
                    <CircularProgress
                      size={16}
                      sx={{
                        color:
                          "inherit",
                      }}
                    />
                  ) : (
                    <Refresh />
                  )
                }
                onClick={
                  handleRefresh
                }
                disabled={
                  loading ||
                  refreshing
                }
                sx={{
                  color: "#fff",
                  borderColor:
                    "rgba(255,255,255,0.25)",
                  borderRadius: 2,
                  px: 2,
                  textTransform:
                    "none",
                  fontWeight: 700,
                  "&:hover": {
                    borderColor:
                      "#fff",
                    background:
                      "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {refreshing
                  ? "Refreshing..."
                  : "Refresh"}
              </Button>
            </Box>
          </Box>

          {/* =================================================
              STAT STRIP
          ================================================= */}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3,1fr)",
              },
              borderBottom:
                "1px solid #e5e7eb",
            }}
          >
            <Box
              sx={{
                p: 2.5,
                borderRight: {
                  sm:
                    "1px solid #e5e7eb",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform:
                    "uppercase",
                  letterSpacing: 1,
                  color: "#94a3b8",
                }}
              >
                Total Customers
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 25,
                  fontWeight: 800,
                  color: "#111827",
                }}
              >
                {loading ? (
                  <Skeleton
                    width={70}
                  />
                ) : (
                  totalCustomers.toLocaleString()
                )}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderRight: {
                  sm:
                    "1px solid #e5e7eb",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform:
                    "uppercase",
                  letterSpacing: 1,
                  color: "#94a3b8",
                }}
              >
                Current Results
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 25,
                  fontWeight: 800,
                  color: "#111827",
                }}
              >
                {loading
                  ? "..."
                  : customers.length}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 2.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform:
                    "uppercase",
                  letterSpacing: 1,
                  color: "#94a3b8",
                }}
              >
                Current Page
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 25,
                  fontWeight: 800,
                  color: "#111827",
                }}
              >
                {page}
                <Typography
                  component="span"
                  sx={{
                    color:
                      "#94a3b8",
                    fontSize: 15,
                    ml: 0.5,
                  }}
                >
                  / {totalPages}
                </Typography>
              </Typography>
            </Box>
          </Box>

          {/* =================================================
              FILTER AREA
          ================================================= */}

          <Box
            sx={{
              p: {
                xs: 2,
                md: 3,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
                gap: 1.5,
                alignItems: {
                  xs: "stretch",
                  md: "center",
                },
              }}
            >
              {/* SEARCH */}

              <TextField
                fullWidth
                value={search}
                onChange={
                  handleSearchChange
                }
                placeholder="Search by name, phone or email..."
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        sx={{
                          color:
                            "#94a3b8",
                        }}
                      />
                    </InputAdornment>
                  ),

                  endAdornment:
                    search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setSearch(
                              ""
                            )
                          }
                        >
                          <Close
                            fontSize="small"
                          />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root":
                    {
                      borderRadius: 2,
                      background:
                        "#f8fafc",
                    },
                }}
              />

              {/* LEVEL */}

              <TextField
                select
                value={level}
                onChange={
                  handleLevelChange
                }
                label="Customer Level"
                size="small"
                sx={{
                  minWidth: {
                    xs: "100%",
                    md: 210,
                  },

                  "& .MuiOutlinedInput-root":
                    {
                      borderRadius: 2,
                    },
                }}
              >
                {LEVELS.map(
                  (item) => (
                    <MenuItem
                      key={
                        item.value ||
                        "all"
                      }
                      value={
                        item.value
                      }
                    >
                      {item.label}
                    </MenuItem>
                  )
                )}
              </TextField>

              {/* CLEAR */}

              {hasFilters && (
                <Button
                  variant="text"
                  onClick={
                    handleClearFilters
                  }
                  sx={{
                    whiteSpace:
                      "nowrap",
                    textTransform:
                      "none",
                    fontWeight: 700,
                    color:
                      "#64748b",
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>

            {/* ACTIVE FILTER */}

            {hasFilters && (
              <Stack
                direction="row"
                spacing={1}
                mt={1.5}
                flexWrap="wrap"
                useFlexGap
              >
                {search.trim() && (
                  <Chip
                    size="small"
                    label={`Search: ${search.trim()}`}
                    onDelete={() =>
                      setSearch(
                        ""
                      )
                    }
                    sx={{
                      fontWeight: 600,
                    }}
                  />
                )}

                {level && (
                  <Chip
                    size="small"
                    label={`Level: ${
                      getLevelConfig(
                        level
                      ).label
                    }`}
                    onDelete={() =>
                      setLevel(
                        ""
                      )
                    }
                    sx={{
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* =================================================
              ERROR
          ================================================= */}

          {error && (
            <Box
              sx={{
                p: 2,
              }}
            >
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={
                      handleRefresh
                    }
                  >
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            </Box>
          )}

          {/* =================================================
              TABLE
          ================================================= */}

          <TableContainer
            sx={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <Table
              sx={{
                minWidth: 1050,
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    background:
                      "#f8fafc",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 800,
                      color:
                        "#475569",
                      fontSize: 12,
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        0.5,
                      py: 1.8,
                    }}
                  >
                    Customer
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                      color:
                        "#475569",
                      fontSize: 12,
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        0.5,
                    }}
                  >
                    Contact
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                      color:
                        "#475569",
                      fontSize: 12,
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        0.5,
                    }}
                  >
                    Email
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 800,
                      color:
                        "#475569",
                      fontSize: 12,
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        0.5,
                    }}
                  >
                    Level
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 800,
                      color:
                        "#475569",
                      fontSize: 12,
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        0.5,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* ==========================================
                    LOADING
                ========================================== */}

                {loading ? (
                  renderSkeletonRows()
                ) : customers.length ===
                  0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{
                        py: 9,
                      }}
                    >
                      <Box
                        sx={{
                          display:
                            "flex",
                          flexDirection:
                            "column",
                          alignItems:
                            "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius:
                              "50%",
                            display:
                              "flex",
                            alignItems:
                              "center",
                            justifyContent:
                              "center",
                            background:
                              "#f1f5f9",
                            mb: 2,
                          }}
                        >
                          <Person
                            sx={{
                              color:
                                "#94a3b8",
                              fontSize: 30,
                            }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            fontWeight: 800,
                            color:
                              "#334155",
                          }}
                        >
                          No customers found
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.5,
                            fontSize: 13,
                            color:
                              "#94a3b8",
                          }}
                        >
                          Try changing your
                          search or level filter.
                        </Typography>

                        {hasFilters && (
                          <Button
                            sx={{
                              mt: 2,
                              textTransform:
                                "none",
                            }}
                            onClick={
                              handleClearFilters
                            }
                          >
                            Clear Filters
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map(
                    (
                      customer,
                      index
                    ) => {
                      const customerLevel =
                        customer.level ||
                        "";

                      const levelConfig =
                        getLevelConfig(
                          customerLevel
                        );

                      return (
                        <TableRow
                          key={
                            customer.id
                          }
                          sx={{
                            transition:
                              "all 0.22s ease",

                            "&:hover":
                              {
                                backgroundColor:
                                  "#fff7ed",
                                boxShadow:
                                  "inset 4px 0 0 #f97316",
                              },
                          }}
                        >
                          {/* CUSTOMER */}

                          <TableCell
                            sx={{
                              py: 2,
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  minWidth: 40,
                                  borderRadius:
                                    "12px",
                                  display:
                                    "flex",
                                  alignItems:
                                    "center",
                                  justifyContent:
                                    "center",
                                  background:
                                    "linear-gradient(135deg,#111827,#334155)",
                                  color:
                                    "#fff",
                                  fontWeight:
                                    800,
                                  fontSize: 14,
                                }}
                              >
                                {customer.full_name
                                  ?.charAt(
                                    0
                                  )
                                  ?.toUpperCase() ||
                                  "C"}
                              </Box>

                              <Box
                                sx={{
                                  minWidth: 0,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight:
                                      800,
                                    color:
                                      "#111827",
                                    whiteSpace:
                                      "nowrap",
                                    overflow:
                                      "hidden",
                                    textOverflow:
                                      "ellipsis",
                                    maxWidth:
                                      240,
                                  }}
                                >
                                  {
                                    customer.full_name
                                  }
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize:
                                      11,
                                    color:
                                      "#94a3b8",
                                    mt: 0.3,
                                  }}
                                >
                                  Customer #
                                  {
                                    customer.id
                                  }
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* CONTACT */}

                          <TableCell>
                            <Typography
                              sx={{
                                fontSize:
                                  13,
                                fontWeight:
                                  600,
                                color:
                                  "#334155",
                              }}
                            >
                              {customer.phone ||
                                "N/A"}
                            </Typography>
                          </TableCell>

                          {/* EMAIL */}

                          <TableCell>
                            <Typography
                              sx={{
                                fontSize:
                                  13,
                                color:
                                  "#64748b",
                                maxWidth:
                                  220,
                                overflow:
                                  "hidden",
                                textOverflow:
                                  "ellipsis",
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {customer.email ||
                                "N/A"}
                            </Typography>
                          </TableCell>

                          {/* LEVEL */}

                          <TableCell>
                            <Chip
                              icon={
                                <WorkspacePremium
                                  sx={{
                                    fontSize:
                                      16,
                                  }}
                                />
                              }
                              label={
                                levelConfig.label
                              }
                              size="small"
                              sx={{
                                fontWeight:
                                  800,
                                color:
                                  levelConfig.color,
                                backgroundColor:
                                  levelConfig.background,
                                border:
                                  `1px solid ${levelConfig.border}`,
                                "& .MuiChip-icon":
                                  {
                                    color:
                                      levelConfig.color,
                                  },
                              }}
                            />
                          </TableCell>

                          {/* ACTIONS */}

                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.8}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Tooltip
                                title="View customer PIN"
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    <Visibility fontSize="small" />
                                  }
                                  onClick={() =>
                                    handleViewPin(
                                      customer
                                    )
                                  }
                                  disabled={
                                    pinLoading
                                  }
                                  sx={{
                                    borderRadius:
                                      1.5,
                                    textTransform:
                                      "none",
                                    fontWeight:
                                      700,
                                  }}
                                >
                                  PIN
                                </Button>
                              </Tooltip>

                              <Tooltip
                                title="Generate a new customer PIN"
                              >
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={
                                    <LockReset fontSize="small" />
                                  }
                                  onClick={() =>
                                    handleResetPin(
                                      customer
                                    )
                                  }
                                  disabled={
                                    pinLoading
                                  }
                                  sx={{
                                    borderRadius:
                                      1.5,
                                    textTransform:
                                      "none",
                                    fontWeight:
                                      700,
                                    color:
                                      "#ea580c",
                                    borderColor:
                                      "#fed7aa",
                                    "&:hover":
                                      {
                                        borderColor:
                                          "#f97316",
                                        background:
                                          "#fff7ed",
                                      },
                                  }}
                                >
                                  Reset
                                </Button>
                              </Tooltip>

                              <Tooltip
                                title="Download royalty card PDF"
                              >
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={
                                    pdfLoadingId ===
                                    customer.id ? (
                                      <CircularProgress
                                        size={
                                          15
                                        }
                                        sx={{
                                          color:
                                            "#fff",
                                        }}
                                      />
                                    ) : (
                                      <Download fontSize="small" />
                                    )
                                  }
                                  onClick={() =>
                                    handleDownloadPDF(
                                      customer
                                    )
                                  }
                                  disabled={
                                    pdfLoadingId !==
                                      null
                                  }
                                  sx={{
                                    borderRadius:
                                      1.5,
                                    textTransform:
                                      "none",
                                    fontWeight:
                                      800,
                                    background:
                                      "#111827",
                                    boxShadow:
                                      "none",
                                    "&:hover":
                                      {
                                        background:
                                          "#f97316",
                                        boxShadow:
                                          "0 6px 18px rgba(249,115,22,0.25)",
                                      },
                                }}
                                >
                                  {pdfLoadingId ===
                                  customer.id
                                    ? "Generating..."
                                    : "Card"}
                                </Button>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    }
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* =================================================
              PAGINATION FOOTER
          ================================================= */}

          {!loading &&
            totalCustomers > 0 && (
              <>
                <Divider />

                <Box
                  sx={{
                    px: {
                      xs: 2,
                      md: 3,
                    },
                    py: 2,
                    display:
                      "flex",
                    flexDirection: {
                      xs: "column",
                      md: "row",
                    },
                    alignItems: {
                      xs: "stretch",
                      md: "center",
                    },
                    justifyContent:
                      "space-between",
                    gap: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      color:
                        "#64748b",
                      textAlign: {
                        xs: "center",
                        md: "left",
                      },
                    }}
                  >
                    Showing{" "}
                    <strong>
                      {firstRecord}
                    </strong>{" "}
                    –
                    <strong>
                      {" "}
                      {lastRecord}
                    </strong>{" "}
                    of{" "}
                    <strong>
                      {totalCustomers.toLocaleString()}
                    </strong>{" "}
                    customers
                  </Typography>

                  {totalPages > 1 && (
                    <Pagination
                      page={page}
                      count={
                        totalPages
                      }
                      onChange={
                        handlePageChange
                      }
                      color="primary"
                      shape="rounded"
                      siblingCount={1}
                      boundaryCount={1}
                      sx={{
                        alignSelf: {
                          xs: "center",
                          md: "auto",
                        },
                        "& .MuiPaginationItem-root":
                          {
                            fontWeight:
                              700,
                          },
                      }}
                    />
                  )}
                </Box>
              </>
            )}
        </Paper>
      </Box>

      {/* =====================================================
          PIN DIALOG
      ===================================================== */}

      <Dialog
        open={openPinModal}
        onClose={
          handleClosePinModal
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow:
              "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background:
              "linear-gradient(135deg,#111827,#1e293b)",
            color: "#fff",
            py: 2.5,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 18,
                }}
              >
                Royalty Card PIN
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color:
                    "rgba(255,255,255,0.65)",
                  mt: 0.3,
                }}
              >
                Authorized access only
              </Typography>
            </Box>

            <IconButton
              onClick={
                handleClosePinModal
              }
              sx={{
                color: "#fff",
              }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            p: 3,
          }}
        >
          {selectedCustomer ? (
            <Box
              textAlign="center"
            >
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  mx: "auto",
                  mb: 1.5,
                  borderRadius: 2,
                  display:
                    "flex",
                  alignItems:
                    "center",
                  justifyContent:
                    "center",
                  background:
                    "#f1f5f9",
                }}
              >
                <LockReset
                  sx={{
                    color:
                      "#f97316",
                    fontSize: 28,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontWeight: 800,
                  color:
                    "#111827",
                }}
              >
                {
                  selectedCustomer.full_name
                }
              </Typography>

              <Typography
                sx={{
                  fontSize: 12,
                  color:
                    "#94a3b8",
                  mt: 0.4,
                }}
              >
                Customer #
                {
                  selectedCustomer.id
                }
              </Typography>

              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg,#f8fafc,#eef2ff)",
                  border:
                    "1px solid #e2e8f0",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color:
                      "#64748b",
                    letterSpacing:
                      1.5,
                  }}
                >
                  CUSTOMER PIN
                </Typography>

                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 36,
                    fontWeight: 900,
                    letterSpacing:
                      10,
                    color:
                      "#111827",
                  }}
                >
                  {selectedCustomer.card_pin ||
                    "----"}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="outlined"
                startIcon={
                  <ContentCopy />
                }
                onClick={
                  handleCopyPin
                }
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  textTransform:
                    "none",
                  fontWeight: 700,
                }}
              >
                Copy PIN
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display:
                  "flex",
                justifyContent:
                  "center",
                py: 5,
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
          }}
        >
          <Button
            onClick={
              handleClosePinModal
            }
            sx={{
              textTransform:
                "none",
              fontWeight: 700,
              color:
                "#64748b",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCustomerTable;